import { useCallback, useEffect, useReducer } from 'react'
import { useTranslation } from 'react-i18next'

import { Grid2 as Grid, Typography } from '@mui/material'

import { useStore } from 'zustand'

import { Divider } from 'src/common/components/Divider'
import { initialState, refreshReducer } from 'src/common/lib'
import { useApiClient } from 'src/common/providers/ApiClientProvider'
import { IClient } from 'src/common/services/client'
import { snackbarUtils } from 'src/common/utils'

import { ClustersFilter, ClustersGrid } from '../components'
import { getClustersAsync } from '../services'
import { useProductClustersStore, useProductClusterStore } from '../stores'

export default function ProductClusters(): JSX.Element {
  const { t } = useTranslation()
  const { apiClient } = useApiClient()
  const { mustRefetchClusters, setClusters, setMustRefetchClusters } =
    useProductClustersStore()
  const resetProductClusterStoreState = useStore(
    useProductClusterStore,
    state => state.resetProductClusterStoreState
  )
  const [state, dispatch] = useReducer(refreshReducer, initialState)

  const fetchData = useCallback(
    async (client: IClient): Promise<void> => {
      if (!client || state.loading) {
        return Promise.resolve()
      }
      try {
        dispatch({ type: 'PENDING' })
        const productClusters = await getClustersAsync(client)
        setClusters(productClusters)
      } catch (error: any) {
        snackbarUtils.error(String(error))
      } finally {
        dispatch({ type: 'RESOLVED' })
      }
    },
    [setClusters, state?.loading]
  )

  useEffect(() => {
    if (apiClient && mustRefetchClusters) {
      fetchData(apiClient)
      setMustRefetchClusters(false)
    }
  }, [apiClient, fetchData, mustRefetchClusters, setMustRefetchClusters])

  useEffect(() => {
    resetProductClusterStoreState()
    setMustRefetchClusters(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Grid size={12} mb={2}>
        <Typography variant={'h2'} style={{ fontWeight: 'bold' }}>
          {t('common.titles.productClusters')}
        </Typography>
      </Grid>
      <Grid size={12}>
        <ClustersFilter data-testid={'clusters-filter'} />
      </Grid>
      <Grid size={12}>
        <Divider />
      </Grid>
      <Grid size={12}>
        <ClustersGrid data-testid={'clusters-grid'} loading={state.loading} />
      </Grid>
    </>
  )
}
